<!-- ShortcutManager.vue -->
<template>
  <shortcut-manager-modal ref="shortcutManagerModalRef" />
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import ShortcutManagerModal from "@/components/common/ShortcutManagerModal.vue";
import router from "@/router/index.js";
import { allowNextReload } from '@/composables/useUnsavedChangesGuard.js';

const shortcutManagerModalRef = ref(null)

const groups = ref([])
const shortcuts = ref([])

// A tecla que abre a ajuda é também a única que continua valendo dentro dela
const COMBO_AJUDA = 'Alt + F1'

const mesmoAtalho = (atalho, keyCombo, group) => atalho.keyCombo === keyCombo && atalho.group === group;

// Registrar de novo substitui em vez de empilhar: checkShortcuts percorre a lista toda e
// chama a ação uma vez por registro, então um atalho duplicado disparava duas vezes — e
// aparecia duas vezes na lista de ajuda.
const registerShortcut = (keyCombo, action, description, group, keyUpAction) => {
  const novo = { keyCombo, action, description, group, keyUpAction };
  const existente = shortcuts.value.findIndex(p => mesmoAtalho(p, keyCombo, group));

  if (existente >= 0) {
    shortcuts.value.splice(existente, 1, novo);
    return;
  }

  shortcuts.value.push(novo);
};


const unregisterShortcut = (keyCombo) => {
  let shortcut = shortcuts.value.find(p => p.keyCombo == keyCombo)

  if(shortcut) {
    shortcuts.value = shortcuts.value.filter(p => p.keyCombo !== keyCombo)

    let shortcutsInGroup = shortcuts.value.filter(p => p.group == shortcut.group)

    if (shortcutsInGroup.length <= 0) {
      groups.value = groups.value.filter(p => p.title !== shortcut.group)
    }
  }
};

// Cinco telas do projeto registram um grupo chamado "Processo". O desenho da ajuda já
// junta grupos de mesmo título, então isto não muda o que aparece na tela — evita a lista
// crescer uma entrada por tela visitada, para sempre.
const registerGroupShortcut = (title, description) => {
  if (groups.value.some(p => p.title === title)) {
    return;
  }

  groups.value.push({ title, description });
};

const estaDigitando = (alvo) =>
  !!alvo?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]');

// Os três ramos abaixo são os que já existiam, movidos para cá sem alteração — inclusive
// o último, cujo `!ctrl == event.ctrlKey` tem cara de engano antigo, mas mexer nele é
// outro assunto. Sair de dentro do forEach é o que mantém a complexidade sob controle.
const combinaComEvento = ({ ctrl, alt, shift, key }, event) => {
  if (key) {
    return ctrl == event.ctrlKey && alt == event.altKey && shift == event.shiftKey &&
      event.key.toLowerCase() === key;
  }

  if (ctrl == event.ctrlKey && alt == event.altKey && shift == event.shiftKey) {
    return true;
  }

  return !ctrl == event.ctrlKey && alt == event.altKey && shift == event.shiftKey;
};

// Função para verificar se a combinação de teclas foi pressionada
const checkShortcuts = (event) => {
  // Com a ajuda aberta a pessoa está lendo, não operando o sistema: ali só o atalho da
  // própria ajuda continua valendo, para fechar. Sem isto, um Ctrl+A para selecionar o
  // texto da busca acionava o atalho de 'Ctrl' registrado pela listagem atrás do modal.
  const naAjuda = !!event.target?.closest?.('.atalhos-modal');
  const digitando = estaDigitando(event.target);

  shortcuts.value.forEach(({ keyCombo, action, keyUpAction }) => {
    if (naAjuda && keyCombo !== COMBO_AJUDA) {
      return;
    }

    const keys = keyCombo.split('+').map(key => key.trim().toLowerCase());
    const ctrl = keys.includes('ctrl');
    const alt = keys.includes('alt');
    const shift = keys.includes('shift');
    const key = keys.find(key => !['ctrl', 'alt', 'shift'].includes(key));

    // Atalho feito só de modificador casa com qualquer tecla apertada junto: 'Ctrl'
    // sozinho disparava num Ctrl+C dentro de um campo. Quem tem tecla própria continua
    // valendo enquanto se digita, senão o Ctrl + Alt + S não salvaria o formulário que a
    // pessoa está preenchendo — que é justamente para isso que ele existe.
    if (!key && digitando) {
      return;
    }

    if (!combinaComEvento({ ctrl, alt, shift, key }, event)) {
      return;
    }

    if (event.type === 'keyup') {
      if (typeof keyUpAction === 'function') {
        keyUpAction();
      }

      return;
    }

    action();
  });
};

onMounted(() => {
  window.addEventListener('keydown', checkShortcuts);
  window.addEventListener('keyup', checkShortcuts);

  registerGroupShortcut('Geral', 'Usados em qualquer lugar')
  registerShortcut(COMBO_AJUDA, () => { onHelp() },'Ajuda', 'Geral');
  registerShortcut('Alt + P', () => { onGoTo('GerencialProcessos') },'Gerência de processos', 'Geral');
});



onBeforeUnmount(() => {
  window.removeEventListener('keydown', checkShortcuts);
  window.removeEventListener('keyup', checkShortcuts);
});



async function onHelp() {
  const modal = shortcutManagerModalRef.value;

  if (!modal) {
    return;
  }

  // Alt + F1 com a ajuda aberta fecha: a tecla que abriu é a que a pessoa tenta de novo
  if (modal.estaAberto?.()) {
    modal.closeModal();
    return;
  }

  await modal.openModal(shortcuts.value, groups.value)
}


function onGoTo(name) {

  if(router.currentRoute.value.name == name) {
    // Recarga da própria tela: não deve acionar o aviso de saída
    allowNextReload();
    window.location.reload();
  }


  router.push({name: name});
}


defineExpose({
  registerShortcut,
  unregisterShortcut,
  registerGroupShortcut,
  // Usado pelo botão de atalhos no cabeçalho, via provide no App.vue
  abrirAjuda: onHelp
});
</script>

